// fddExport.js — Excel sheet for the field dry density (core cutter), laid out like the lab's "FDD" sheet.
// The lab sheet shows one set of values; this one has a column per determination plus an Average column.
const THIN = { style: 'thin', color: { argb: 'FF000000' } };
const BOX = { top: THIN, left: THIN, bottom: THIN, right: THIN };
const font = (size = 12, bold = false) => ({ name: 'Calibri', size, bold });
const utcDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

export function addFDDSheet(wb, { header, inputs, result }) {
  const dets = inputs.dets, nD = dets.length;
  const last = 2 + nD;                      // columns: A label | B.. determinations | last = Average
  const ws = wb.addWorksheet('FDD', {
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 1 },
  });
  ws.getColumn(1).width = 46.3;
  for (let c = 2; c <= last; c++) ws.getColumn(c).width = 19;
  const col = (c) => ws.getColumn(c).letter;

  const left = { horizontal: 'left', vertical: 'middle', wrapText: true };
  const center = { horizontal: 'center', vertical: 'middle', wrapText: true };
  const area = (r1, c1, r2, c2, value, o = {}) => {
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
      const cell = ws.getCell(r, c);
      cell.border = o.border ?? BOX; cell.font = o.font ?? font(); cell.alignment = o.align ?? center;
    }
    const top = ws.getCell(r1, c1);
    if (value !== undefined && value !== null && value !== '') top.value = value;
    if (o.numFmt) top.numFmt = o.numFmt;
    if (r2 > r1 || c2 > c1) ws.mergeCells(r1, c1, r2, c2);
  };
  let r = 0;
  // label in A, one merged value across B..last
  const single = (label, value, numFmt, bold = false) => {
    r += 1; ws.getRow(r).height = 30;
    area(r, 1, r, 1, label, { font: font(14, true), align: left });
    area(r, 2, r, last, value, { font: font(14, bold), numFmt });
    return r;
  };
  // label in A, one value per determination (+ optional Average cell)
  const perDet = (label, valueFor, numFmt, avg, o = {}) => {
    r += 1; ws.getRow(r).height = o.height ?? 31.5;
    area(r, 1, r, 1, label, { font: font(14, !!o.boldLabel), align: left });
    dets.forEach((d, i) => area(r, 2 + i, r, 2 + i, valueFor(d, i, col(2 + i)), { font: font(14, !!o.bold), numFmt }));
    area(r, last, r, last, avg ?? '', { font: font(14, true), numFmt: o.avgFmt ?? numFmt });
    return r;
  };

  ws.getRow(1).height = 39.8; r = 1;
  area(1, 1, 1, 1, 'Name of Project:-', { font: font(14, true), align: left });
  area(1, 2, 1, last, header.project, { font: font(12), align: left });
  single('Type of Material', header.material);
  single('Source of Material', inputs.source);
  single('Date of Testing', utcDate(header.testedOn), 'dd/mm/yyyy');
  single('Layer No.', inputs.layer);
  single('Test Location', header.location);
  single('Side ', inputs.side);
  single('Sample No.', header.sampleNo);
  const rMdd = single('Lab M.D.D (gm/cc)', inputs.labMddGcc, '0.000');
  single('OMC (%)', inputs.omcPct ?? '', '0.00');
  single('Passing 4.75 mm sieve (%)', inputs.passing475Pct, '0.0');
  single('Core cutter dia × height (cm)', `${inputs.diameterCm} × ${inputs.heightCm}`);
  const rVol = single('Volume of Core Cutter (cc)', inputs.volumeCc, '0.0');

  // determinations
  r += 1; ws.getRow(r).height = 28;
  area(r, 1, r, 1, 'Determination no.', { font: font(14, true), align: left });
  dets.forEach((_, i) => area(r, 2 + i, r, 2 + i, i + 1, { font: font(14, true) }));
  area(r, last, r, last, 'Average', { font: font(14, true) });

  const rCut = perDet('Wieght of Core Cutter (gms)', (d) => d.cutterG, '0');
  const rCutSoil = perDet('Wieght of Core Cutter + wet Soil (gms)', (d) => d.cutterSoilG, '0');
  const rSoil = perDet('Wieght of Soil (gms)', (d, i, L) => ({ formula: `${L}${rCutSoil}-${L}${rCut}`, result: d.cutterSoilG - d.cutterG }), '0');
  const rWet = perDet('wet Density (g/cc)', (d, i, L) => ({ formula: `${L}${rSoil}/$B$${rVol}`, result: result.rows[i].bulk }), '0.000',
    { formula: `AVERAGE(B${r + 1}:${col(last - 1)}${r + 1})`, result: result.wetDensityAvg });
  const rCw = perDet('Container + wet soil (gms)', (d) => d.wetG, '0.00');
  const rCd = perDet('Container + dry soil (gms)', (d) => d.dryG, '0.00');
  const rCt = perDet('Container (gms)', (d) => d.tareG, '0.00');
  const rMc = perDet('Moister Content (%)', (d, i, L) => ({ formula: `(${L}${rCw}-${L}${rCd})/(${L}${rCd}-${L}${rCt})*100`, result: result.rows[i].w }), '0.0',
    { formula: `AVERAGE(B${r + 1}:${col(last - 1)}${r + 1})`, result: result.mcAvg });
  const rDry = perDet('Dry Density (g/cc)', (d, i, L) => ({ formula: `${L}${rWet}*100/(100+${L}${rMc})`, result: result.rows[i].dry }), '0.000',
    { formula: `ROUND(AVERAGE(B${r + 1}:${col(last - 1)}${r + 1}),2)`, result: result.dryDensity }, { avgFmt: '0.00', boldLabel: true });

  r += 1; ws.getRow(r).height = 31.5;
  area(r, 1, r, 1, 'Compaction ( %)', { font: font(14, true), align: left });
  area(r, 2, r, last, { formula: `AVERAGE(B${rDry}:${col(last - 1)}${rDry})/B${rMdd}*100`, result: result.compactionPct },
    { font: font(14, true), numFmt: '0.00' });

  r += 2;
  area(r, 1, r, last, 'Moisture content of each determination is from its own container; dry density = wet density × 100 ÷ (100 + moisture content). Reported dry density is the average, to 0.01 g/cc.',
    { font: font(9), align: left, border: {} });
  ws.getRow(r).height = 26;
  r += 1; ws.getRow(r).height = 16;
  area(r, 1, r, last, `Checked by: ${header.checkedBy}   |   Tested by: ${header.testedBy}`, { font: font(9), align: left, border: {} });
  r += 2;
  ws.getRow(r).height = 44;
  area(r, 1, r, last, 'Authorised Signatory', { font: font(12, true), align: { horizontal: 'right', vertical: 'middle' }, border: {} });
  return ws;
}

export const fddExporter = {
  async addSheet(wb, { header, data }) {
    addFDDSheet(wb, { header, inputs: data.inputs, result: data.result });
    return []; // no graph in the FDD template
  },
};
